import { useEffect, useRef, useMemo } from 'react';
import { props, values, map, pipe, length, has, isEmpty } from 'ramda';

import {
  useAllTarmeds,
  ID,
  DATE,
  QUANTITY,
  CODE,
  REF_CODE,
  REF_CODE_OUTDATED,
  SESSION,
  EXCLUDES,
  BODY_LOCATION,
  SUB_TARDOC,
  SUB_TARMED,
  WZ,
  WZ_MIN,
} from 'store/invoices';

export const useGetScrollWidth = () => {
  const width = useRef();

  useEffect(() => {
    const div = document.createElement('div');

    div.style.overflowY = 'scroll';
    div.style.width = '25px';
    div.style.height = '25px';
    div.style.opacity = '0';

    document.body.append(div);
    width.current = div.offsetWidth - div.clientWidth;
    div.remove();
  }, []);

  return width.current;
};

const getComparedList = (services, tarmedsObject, defaultDate) =>
  isEmpty(tarmedsObject)
    ? []
    : services.map((service = {}) => {
        const key = service[SUB_TARDOC] || service[SUB_TARMED];

        return {
          ...tarmedsObject[key],
          [SESSION]: service[SESSION],
          [DATE]: service[DATE] || defaultDate || '',
          [QUANTITY]: service[QUANTITY],
        };
      });
const getErrorMessage = ({ code, session }) => `Die Tarifposition ${code} wurde für die Session ${session} mehrfach erfasst.`;
const getRefErrorMessage = ({ code, refCode }) =>
  `Um die Tarifposition ${code} zu verrechnen, muss auch die Tarifposition ${refCode} erfasst werden.`;
const getSessionErrorMessage = ({ code, sessionCodeError }) =>
  `Die Tarifposition ${code} darf nicht in der gleichen Session wie ${sessionCodeError} verrechnet werden.`;
const getIncorrect1100ErrorMessage = () =>
  'Verwenden Sie die Tarifposition «Fundusaufnahmen, beidseitig» (RC.35.0110), um eine Fundusaufnahme für beide Augen zu erfassen.';
const getIncorrect1120ErrorMessage = () =>
  'Verwenden Sie die Tarifposition «Funduspanorama, beidseitig» (RC.35.0130), um ein Funduspanorama für beide Augen zu erfassen.';
const getIsWzNeedMessage = ({ [WZ]: wz, [SESSION]: session }) =>
  `Die Position ${wz} (Wechselzeit) darf in Session ${session} ebenfalls verrechnet werden.`;
const getIsWzNeedRefMessage = ({ [CODE]: code, [SESSION]: session }) =>
  `Die Position ${code} darf in der Session ${session} nicht verrechnet werden.`;
const comparator = (acc, items = []) => {
  items.forEach((item, index) => {
    if (!acc[item.index]) acc[item.index] = [];
    if (item.refCodeError) acc[item.index].push(getRefErrorMessage(item));

    const match = items.find(
      ({ compareDate, session }, i) => i !== index && compareDate === item.compareDate && item.session === session
    );

    if (match && !acc[match.index]) acc[match.index] = [];
    if (match) {
      acc[item.index].push(getErrorMessage(item));
      acc[match.index].push(getErrorMessage(match));
    }

    if (item.sessionCodeError) acc[item.index].push(getSessionErrorMessage(item));
    if (item.code1100) acc[item.index].push(getIncorrect1100ErrorMessage());
    if (item.code1120) acc[item.index].push(getIncorrect1120ErrorMessage());
    if (item.needWz) acc[item.index].push(getIsWzNeedMessage(item.needWz));
    if (item.needWzRef) acc[item.index].push(getIsWzNeedRefMessage(item.needWzRef));
  });

  return acc;
};
const validateList = (list = []) => {
  const extracted = list.reduce((acc, service, index) => {
    const [id, date, code, refCode, session, excludes, bodyLocation, wz] = props(
      [ID, DATE, CODE, service[SUB_TARMED] ? REF_CODE_OUTDATED : REF_CODE, SESSION, EXCLUDES, BODY_LOCATION, WZ],
      service || {}
    );
    if (!acc[id]) acc[id] = [];

    const errors = {
      refCodeError: Boolean(refCode),
      sessionCodeError: null,
      code1100: null,
      code1120: null,
    };
    const amount = { wz: 0, wzRef: 0 };
    const isWzRef = has(WZ_MIN, service);

    list.forEach((item) => {
      if (refCode && item[CODE] === refCode) errors.refCodeError = false;
      if (!errors.sessionCodeError && excludes?.length && excludes.includes(item[CODE]) && session === item[SESSION])
        errors.sessionCodeError = item[CODE];
      if (
        !errors.sessionCodeError &&
        session === item[SESSION] &&
        ((code === 'RC.35.0100' && item[CODE] === 'RC.35.0120') || (code === 'RC.35.0120' && item[CODE] === 'RC.35.0100'))
      ) {
        errors.sessionCodeError = item[CODE];
      }

      if (
        code === 'RC.35.0100' &&
        session === item[SESSION] &&
        (item[CODE] === 'RC.35.0110' || (item[CODE] === code && bodyLocation !== item[BODY_LOCATION]))
      ) {
        errors.code1100 = item[CODE];
      }

      if (
        code === 'RC.35.0120' &&
        session === item[SESSION] &&
        (item[CODE] === 'RC.35.0130' || (item[CODE] === code && bodyLocation !== item[BODY_LOCATION]))
      ) {
        errors.code1120 = item[CODE];
      }

      if (code === 'RC.35.0110' && session === item[SESSION] && item[CODE] === 'RC.35.0100') errors.code1100 = item[CODE];
      if (code === 'RC.35.0130' && session === item[SESSION] && item[CODE] === 'RC.35.0120') errors.code1120 = item[CODE];
      if (wz && item[CODE] === wz && item[SESSION] === session) amount.wz += 1;
      if (isWzRef && item[WZ] && item[WZ] === code && item[SESSION] === session) amount.wzRef += 1;
    });

    if (wz && amount.wz < 1) errors.needWz = service;
    if (isWzRef && amount.wzRef < 1) errors.needWzRef = service;

    acc[id].push({
      index,
      date,
      session,
      compareDate: date.replace(/T.+/, ''),
      code,
      refCode,
      ...errors,
    });

    return acc;
  }, {});

  return values(extracted).reduce(comparator, {});
};

export const useValidationTarmeds = (services, defaultDate) => {
  const allTarmeds = useAllTarmeds();
  const list = useMemo(() => getComparedList(services, allTarmeds, defaultDate), [services, allTarmeds, defaultDate]);
  const allErrors = useMemo(() => validateList(list), [list]);
  const errors = map(pipe(length, Boolean), allErrors);
  const errorMessages = useMemo(
    () =>
      values(allErrors).reduce((acc, messages) => {
        messages.forEach((msg) => {
          if (!acc.includes(msg)) acc.push(msg);
        });

        return acc;
      }, []),
    [allErrors]
  );

  return [list, errors, errorMessages, isEmpty(allTarmeds)];
};
